// TRUST-RECON-1 v1 (2026-06-09) — Operations → Trust Reconciliation.
// Read-only. Two independent views of trust money per account — the Atticus
// LEDGER (attribution sourced from VCA Payment History + payment coding) vs the
// QBO BANK cash record (read-only sync) — plus the VARIANCE to reconcile.
// Aggregate v1; per-transaction matching arrives with Operating QBO + the
// matcher (TRUST-RECON-2). Server-gated manage_reimbursement_intake.
import { useState, useEffect } from "react";
import { apiFetch, fmt } from "../api";
import { Spinner } from "./ui";  // UI-SWEEP-ACCOUNTING

const mono = { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" };

function VarCell({ v }) {
  const zero = Math.abs(Number(v) || 0) < 0.005;
  return (
    <td style={{ ...mono, textAlign: "right", padding: "6px 12px",
                 color: zero ? "var(--text2)" : "#b45309", fontWeight: zero ? 400 : 700 }}>
      {fmt.money(v)}
    </td>
  );
}

export default function TrustReconciliation() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch("/reports/trust-reconciliation")
      .then((d) => { setData(d); setLoading(false); })
      .catch((e) => { setErr(String((e && e.message) || e)); setLoading(false); });
  }, []);

  if (loading) return <div className="card"><Spinner label="Loading trust reconciliation…" /></div>;
  if (err) return <div className="card"><div className="card-body" style={{ color: "#b91c1c" }}>Error: {err}</div></div>;
  if (!data) return null;

  const { accounts = [], qbo = {}, variance, v1_note } = data;
  const th = { textAlign: "right", padding: "6px 12px", fontSize: 11, textTransform: "uppercase",
               color: "white", borderBottom: "1px solid var(--border)", whiteSpace: "nowrap" };
  const thL = { ...th, textAlign: "left" };
  const td = { ...mono, textAlign: "right", padding: "6px 12px", whiteSpace: "nowrap" };

  return (
    <div>
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3>Trust Reconciliation</h3>
          <span style={{ fontSize: 12, fontWeight: 600, color: qbo.connected ? "#166534" : "#b91c1c" }}>
            {qbo.connected ? "● QBO Trust connected" : "○ QBO not connected"}
          </span>
        </div>
        <div className="card-body" style={{ fontSize: 12, color: "var(--text2)", lineHeight: 1.6 }}>
          Last QBO sync: <strong>{fmt.datetime(qbo.last_sync_at)}</strong>
          {" · "}{qbo.rows != null ? qbo.rows.toLocaleString() : "—"} staged bank transactions
          {qbo.date_min ? <> ({fmt.date(qbo.date_min)} – {fmt.date(qbo.date_max)})</> : null}
        </div>
      </div>

      {variance && (
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="card-header"><h3>IA General Trust — Ledger vs Bank</h3></div>
          <div className="card-body" style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={thL}></th>
                  <th style={th}>Atticus Ledger</th>
                  <th style={th}>QBO Bank</th>
                  <th style={th}>Variance</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: "1px solid var(--border)" }}>
                  <td style={{ padding: "6px 12px", fontWeight: 600 }}>Money In</td>
                  <td style={td}>{fmt.money(variance.ledger_in)}</td>
                  <td style={td}>{fmt.money(variance.qbo_in)}</td>
                  <VarCell v={variance.in_variance} />
                </tr>
                <tr style={{ borderBottom: "1px solid var(--border)" }}>
                  <td style={{ padding: "6px 12px", fontWeight: 600 }}>Money Out</td>
                  <td style={td}>{fmt.money(variance.ledger_out)}</td>
                  <td style={td}>{fmt.money(variance.qbo_out)}</td>
                  <VarCell v={variance.out_variance} />
                </tr>
                <tr style={{ borderTop: "2px solid var(--border)" }}>
                  <td style={{ padding: "8px 12px", fontWeight: 700 }}>Balance / Net</td>
                  <td style={{ ...td, fontWeight: 700 }}>{fmt.money(variance.ledger_balance)}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{fmt.money(variance.qbo_net)}</td>
                  <VarCell v={variance.balance_variance} />
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-header"><h3>Trust Ledger by Account</h3></div>
        <div className="card-body" style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr>
                <th style={thL}>Account</th>
                <th style={thL}>Kind</th>
                <th style={th}>Ledger In</th>
                <th style={th}>Ledger Out</th>
                <th style={th}>Balance</th>
                <th style={th}>Rows</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((a) => (
                <tr key={a.id} style={{ borderBottom: "1px solid var(--border)" }}>
                  <td style={{ padding: "6px 12px" }}>{a.account_name}</td>
                  <td style={{ padding: "6px 12px", color: "var(--text2)" }}>{a.trust_account_kind || "—"}</td>
                  <td style={td}>{fmt.money(a.ledger_in)}</td>
                  <td style={td}>{fmt.money(a.ledger_out)}</td>
                  <td style={{ ...td, fontWeight: 600 }}>{fmt.money(a.ledger_balance)}</td>
                  <td style={td}>{(a.ledger_rows || 0).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {v1_note && (
        <p style={{ fontSize: 12, color: "var(--text2)", lineHeight: 1.6, fontStyle: "italic" }}>{v1_note}</p>
      )}
    </div>
  );
}
